/**
 * Date and time formatting helpers.
 * Uses native Date API and Intl — no external dependency.
 */

/**
 * Returns the local UTC offset as a "+HHMM" / "-HHMM" string.
 * @param {Date} date
 * @returns {string}
 */
function utcOffset(date) {
  const totalMinutes = -date.getTimezoneOffset();
  const sign = totalMinutes >= 0 ? "+" : "-";
  const abs  = Math.abs(totalMinutes);
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}${String(abs % 60).padStart(2, "0")}`;
}

/**
 * Extract zero-padded date/time components from a date value.
 * @param {string|number|Date} value
 * @returns {{ d: Date, day: string, month: string, year: number, hh: string, mi: string, ss: string }}
 */
function dateParts(value) {
  const d = new Date(String(value));
  return {
    d,
    day:   String(d.getDate()).padStart(2, "0"),
    month: String(d.getMonth() + 1).padStart(2, "0"),
    year:  d.getFullYear(),
    hh:    String(d.getHours()).padStart(2, "0"),
    mi:    String(d.getMinutes()).padStart(2, "0"),
    ss:    String(d.getSeconds()).padStart(2, "0")
  };
}

/**
 * Converts a raw byte count to a human-readable string (e.g. 1536 → "1.5 KB").
 * Exported as a standalone function for unit-testability.
 * @param {number} bytes
 * @returns {string}
 */
export function formatBytes(bytes) {
  if (!bytes || bytes === 0) return "0 B";
  const UNITS = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < UNITS.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  return `${Number(value.toFixed(1))} ${UNITS[unitIndex]}`;
}

/**
 * Default exports
 */
export default {
  methods: {
    dates_date(value, separator = "/") {
      if (!value) return undefined;
      const { day, month, year } = dateParts(value);
      return `${day}${separator}${month}${separator}${year}`;
    },

    dates_dateSmall(value, separator = "/") {
      if (!value) return undefined;
      const { day, month } = dateParts(value);
      return `${day}${separator}${month}`;
    },

    dates_dateTime(value, separator = "/") {
      if (!value) return undefined;
      const { day, month, year, hh, mi } = dateParts(value);
      return `${day}${separator}${month}${separator}${year} ${hh}:${mi}`;
    },

    dates_dateTimeSmall(value, separator = "/") {
      if (!value) return undefined;
      const { day, month, hh, mi } = dateParts(value);
      return `${day}${separator}${month} ${hh}:${mi}`;
    },

    dates_dateTimeFull(value, separator = "/") {
      if (!value) return undefined;
      const { d, day, month, year, hh, mi, ss } = dateParts(value);
      return `${day}${separator}${month}${separator}${year} ${hh}:${mi}:${ss} UTC${utcOffset(d)}`;
    },

    dates_dateTimeExtended(value, separator = "/") {
      if (!value) return undefined;
      const { day, month, year, hh, mi } = dateParts(value);
      return `il ${day}${separator}${month}${separator}${year} alle ${hh}:${mi}`;
    },

    dates_time(value) {
      if (!value) return undefined;
      const { hh, mi } = dateParts(value);
      return `${hh}:${mi}`;
    },

    /** @param {number} bytes */
    format_bytes(bytes) {
      return formatBytes(bytes);
    },

    /** @param {string} isoDate */
    format_relative(isoDate) {
      const diffMs  = Date.now() - new Date(isoDate).getTime();
      const abs     = Math.abs(diffMs);
      const sign    = diffMs >= 0 ? -1 : 1;
      const rtf     = new Intl.RelativeTimeFormat("it", { numeric: "auto" });
      const seconds = Math.round(abs / 1000);
      if (seconds < 60)  return rtf.format(sign * seconds, "second");
      const minutes = Math.round(seconds / 60);
      if (minutes < 60)  return rtf.format(sign * minutes, "minute");
      const hours   = Math.round(minutes / 60);
      if (hours   < 24)  return rtf.format(sign * hours,   "hour");
      return rtf.format(sign * Math.round(hours / 24), "day");
    }
  }
};
